// OrderServicesForm — the quote-level "What do you need us to do with it?"
// section (configurator step 14). After the customer configures their
// product(s), they pick — once per order — how the finished product leaves us:
// one of Shipping / Pickup / Fulfillment. The selected mode reveals its own
// subsection.
//
// Assembly / kitting is not part of this section. Kitting is an `assembly` item —
// one of the product grid's tiles — whose kit is authored in that item's own
// editor (ADR-020), so the work is described with the items and this section
// asks only how the finished order leaves.
//
// A dedicated QUOTE-LEVEL surface (one per order), modeled on ShipToForm: each
// input's DOM id EQUALS its field key (`fulfillment.<prop>`,
// `order_services.shipping_service`) so QuoteEdit's reveal/scroll + the shared
// `errors` map light up inline. Intent-capture only — NO price is shown or
// computed here (the gate enforces completeness at submit; Nexus quotes/routes
// the services).
//
// Shipping reuses ShipToForm for its addresses. Shipping, Fulfillment and Pickup
// are three MUTUALLY-EXCLUSIVE delivery modes, each STORED in services_requested
// when the customer clicks its card. None lit means the customer has not answered
// yet — never a pickup — and the submit gate refuses the order on
// DELIVERY_MODE_FIELD_KEY, which this grid carries as its DOM id.
// Choosing Shipping reveals the address. Pickup and Fulfillment both drop the
// address requirement (a fulfilled order goes into stock under a SKU and leaves
// against the fulfillment agreement, not against one address on this quote), but
// only Pickup stamps delivery_method='pickup' — every other mode stamps 'ship', so
// nothing but a clicked Pickup card ever reaches Nexus as a pickup. The stamp is
// the shared reconcileDeliveryIntent's answer for the mode,
// applied to EVERY address here so the list in the editor already matches what
// the server will store.
//
// A physical proof (a mailed printed sample) can't ride a self-pickup order, so
// when the quote carries one (physicalProofSelected) the Pickup card is disabled
// and — if Pickup was active — Shipping is forced on. Fulfillment still ships, so
// it may carry a proof.
//
// That same flag opens the printed-sample block (ProofDeliveryForm, FB-0036):
// how many samples, and where each is mailed. It sits ABOVE the three delivery
// subsections rather than inside Shipping, because a sample is mailed whichever
// way the finished order leaves the building.

import { useEffect, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import {
  APPLY_BARCODE_FIELD_KEY,
  DELIVERY_MODE_FIELD_KEY,
  ORDER_SERVICES,
  ORDER_SERVICE_LABELS,
  SHIPPING_SERVICES,
  SHIPPING_SERVICE_LABELS,
  THIRD_PARTY_ACCOUNT_FIELD_KEY,
  THIRD_PARTY_ACCOUNT_MAX_LENGTH,
  reconcileDeliveryIntent,
  resolveDeliveryMode,
  type FulfillmentSnapshot,
  type LineFulfillmentTarget,
  type OrderService,
  type OrderServicesSnapshot,
  type ShipTo,
  type ShippingService,
} from 'shared';

import { readBackValue } from './FieldReadBack.js';
import { useFieldDomId } from './fieldDomId.js';
import { FulfillmentFields, type FulfillmentItem } from './FulfillmentFields.js';
import { EnumRadios } from './KittingFields.js';
import { OrderSubsection } from './OrderSubsection.js';
import { ProofDeliveryForm } from './ProofDeliveryForm.js';
import { ShipToForm } from './ShipToForm.js';
import { localizeVocabLabels } from './vocabLabels.js';

type OrderServicesFormProps = {
  value: OrderServicesSnapshot;
  onChange: (next: OrderServicesSnapshot) => void;
  // Where the order ships, owned by QuoteEdit; this component re-stamps
  // delivery_method when the mode changes and hands the list to ShipToForm.
  shipTos: ShipTo[];
  onShipTosChange: (next: ShipTo[]) => void;
  // The order quantity a split must add up to, or null when the order has no
  // single correct total. Passed through to the address list for its
  // reconciliation line. A unit count, never a price.
  orderQuantity?: number | null;
  // True when the quote resolves to a physical proof. Pickup is then disallowed
  // (a mailed sample needs a carrier), Shipping is forced on, and the printed
  // sample's own count + destinations open up above the delivery subsections.
  physicalProofSelected?: boolean;
  // Every item on the order, owned by QuoteEdit like every line. Fulfillment
  // stocks each one under its own SKU, and the answer is patched back onto the
  // item's own line.
  fulfillmentItems?: readonly FulfillmentItem[];
  onPatchItemFulfillmentTarget?: (
    localId: string,
    target: LineFulfillmentTarget | undefined,
  ) => void;
  // The printed proof sample (FB-0036): how many, and where each is mailed.
  // Owned by QuoteEdit like the order's own addresses; this component only
  // decides WHEN the block is on screen. Its counts balance against the sample
  // count, never the order quantity.
  proofQuantity?: number | null;
  onProofQuantityChange: (next: number | null) => void;
  proofShipTos: ShipTo[];
  onProofShipTosChange: (next: ShipTo[]) => void;
  // Field-keyed inline errors (e.g. 'fulfillment.barcode_resolution', 'ship_to.city'),
  // shared with the near-button summary.
  errors: Record<string, string>;
  disabled?: boolean;
  // A control for the items above, pinned to this card's top-right corner: the
  // first place the customer lands after the last item.
  headerAction?: ReactNode;
};

// Icon for each order service — same single-color 80×80 visual language as the
// product-picker FamilyGlyph cards, so the bottom "what do you need us to do
// with it?" buttons read as the same control family as the top product picker.
// Exhaustive over OrderService, so adding a service is a compile error here.
function OrderServiceGlyph({ service }: { service: OrderService }): JSX.Element {
  switch (service) {
    case 'pickup':
      return <PickupGlyph />;
    case 'shipping':
      // Delivery truck.
      return (
        <svg viewBox="0 0 80 80" aria-hidden="true">
          <rect x="10" y="30" width="34" height="26" fill="none" stroke="currentColor" strokeWidth="2" />
          <path d="M44 36 L58 36 L68 46 L68 56 L44 56 Z" fill="none" stroke="currentColor" strokeWidth="2" />
          <path d="M48 40 L57 40 L63 46 L48 46 Z" fill="none" stroke="currentColor" strokeWidth="1.5" />
          <circle cx="24" cy="58" r="5" fill="none" stroke="currentColor" strokeWidth="2" />
          <circle cx="56" cy="58" r="5" fill="none" stroke="currentColor" strokeWidth="2" />
        </svg>
      );
    case 'fulfillment':
      // Warehouse: roof + body + roll-up door + two stored cartons.
      return (
        <svg viewBox="0 0 80 80" aria-hidden="true">
          <path d="M12 34 L40 20 L68 34" fill="none" stroke="currentColor" strokeWidth="2" />
          <rect x="16" y="34" width="48" height="30" fill="none" stroke="currentColor" strokeWidth="2" />
          <rect x="35" y="48" width="10" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" />
          <rect x="22" y="44" width="8" height="8" fill="none" stroke="currentColor" strokeWidth="1.5" />
          <rect x="50" y="44" width="8" height="8" fill="none" stroke="currentColor" strokeWidth="1.5" />
        </svg>
      );
  }
}

// Icon for the Pickup card — a hand-truck / dolly loaded with cartons, reading
// as "you'll come get it / bring your own carrier". Same single-color 80×80
// visual language as OrderServiceGlyph so it sits in the same card family, and
// distinct from the shipping truck + fulfillment warehouse.
function PickupGlyph(): JSX.Element {
  return (
    <svg viewBox="0 0 80 80" aria-hidden="true">
      {/* Cartons stacked on the dolly. */}
      <rect x="32" y="16" width="26" height="18" fill="none" stroke="currentColor" strokeWidth="2" />
      <rect x="32" y="34" width="26" height="16" fill="none" stroke="currentColor" strokeWidth="2" />
      {/* Upright frame + angled handle. */}
      <line x1="28" y1="12" x2="28" y2="56" stroke="currentColor" strokeWidth="2" />
      <line x1="28" y1="12" x2="36" y2="12" stroke="currentColor" strokeWidth="2" />
      {/* Toe plate the cartons rest on. */}
      <line x1="28" y1="56" x2="48" y2="56" stroke="currentColor" strokeWidth="2" />
      {/* Wheel. */}
      <circle cx="32" cy="60" r="5" fill="none" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

// The optional barcode-label add-on, asked on Shipping and on Pickup. One control
// over one stored answer for both modes: it is about the pieces, not how they
// leave, so switching between the two keeps it. Fulfillment asks its own,
// mandatory barcode question instead (FulfillmentFields).
//
// A read-back states the add-on only when it was ticked — an unticked box is not
// an answer — and as text, not a disabled input, so it stays reachable by
// keyboard and screen reader (the purchased-labels box's reason, AssemblyControl).
function ApplyBarcodeCheckbox({
  checked,
  onToggle,
  disabled,
}: {
  checked: boolean;
  onToggle: (next: boolean) => void;
  disabled?: boolean;
}): JSX.Element | null {
  const { t } = useTranslation(['quote', 'common']);
  const domId = useFieldDomId(APPLY_BARCODE_FIELD_KEY);
  if (disabled) {
    return checked ? <p className="value--locked">{t('orderServices.applyBarcode.label')}</p> : null;
  }
  return (
    <label className="checkbox-row">
      <input
        id={domId}
        type="checkbox"
        checked={checked}
        onChange={(e) => onToggle(e.target.checked)}
      />
      <span>
        {t('orderServices.applyBarcode.label')}
        <span className="help help--block">{t('orderServices.applyBarcode.help')}</span>
      </span>
    </label>
  );
}

export function OrderServicesForm({
  value,
  onChange,
  shipTos,
  onShipTosChange,
  orderQuantity,
  physicalProofSelected = false,
  fulfillmentItems = [],
  onPatchItemFulfillmentTarget,
  proofQuantity,
  onProofQuantityChange,
  proofShipTos,
  onProofShipTosChange,
  errors,
  disabled,
  headerAction,
}: OrderServicesFormProps): JSX.Element {
  const { t } = useTranslation(['quote', 'common']);
  const fulfillment: FulfillmentSnapshot = value.fulfillment ?? {};
  const modeError = errors[DELIVERY_MODE_FIELD_KEY];

  // The ONE active delivery mode, or null while the customer has not picked one.
  // Read through the shared resolver so a value carrying two (a quote saved under
  // the old multi-select behavior) still lights one card — the same one every gate
  // and feed reads.
  const mode = resolveDeliveryMode(value);
  const isShipping = mode === 'shipping';
  const isFulfillment = mode === 'fulfillment';
  const isPickup = mode === 'pickup';
  const shippingServiceLabels = localizeVocabLabels(
    'orderServices.shipping.serviceOptions',
    SHIPPING_SERVICE_LABELS,
    t,
  );

  // Store the picked mode as the whole of services_requested — pickup included,
  // as an answer in its own right — and re-stamp every address through the shared
  // reconcile, so the editor's list says what the server will store.
  function setDeliveryMode(next: OrderService): void {
    const services: OrderServicesSnapshot = { ...value, services_requested: [next] };
    onChange(services);
    onShipTosChange(
      reconcileDeliveryIntent(
        shipTos.length === 0 ? [{ delivery_method: 'ship' }] : shipTos,
        services,
      ),
    );
  }

  // Each card is radio-like: clicking the already-active mode is a no-op (you
  // switch away by picking another). Pickup is blocked while a physical proof is
  // on the quote (it needs a carrier).
  function choose(next: OrderService): void {
    if (mode === next) return;
    if (next === 'pickup' && physicalProofSelected) return;
    setDeliveryMode(next);
  }

  // A physical proof (mailed sample) can't ride a self-PICKUP order — it needs a
  // carrier. Fulfillment still ships (to a list / from storage), so it may carry
  // a proof; only Pickup is disallowed. If the customer was on Pickup and a
  // physical proof appears upstream, force Shipping back on so the order stays
  // deliverable. Fires only on the transition into physical-proof-with-pickup.
  useEffect(() => {
    if (disabled || !physicalProofSelected || !isPickup) return;
    setDeliveryMode('shipping');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled, physicalProofSelected, isPickup]);

  function patchFulfillment(patch: Partial<FulfillmentSnapshot>): void {
    onChange({ ...value, fulfillment: { ...fulfillment, ...patch } });
  }

  // The carrier account belongs to `customer_labels` alone, so any other pick
  // drops it rather than keeping a hidden answer (the save does the same).
  function pickShippingService(next: ShippingService): void {
    const { third_party_account: _account, ...rest } = value;
    onChange(
      next === 'customer_labels'
        ? { ...value, shipping_service: next }
        : { ...rest, shipping_service: next },
    );
  }

  // Unticked is absent, never `false`: the snapshot stores only a request.
  function toggleApplyBarcode(next: boolean): void {
    onChange({ ...value, apply_barcode: next ? true : undefined });
  }
  const applyBarcode = (
    <ApplyBarcodeCheckbox
      checked={value.apply_barcode === true}
      onToggle={toggleApplyBarcode}
      disabled={disabled}
    />
  );

  return (
    <div className="card">
      <div className="order-services__header">
        <h3 className="order-services__title">{t('orderServices.title')}</h3>
        {headerAction}
      </div>
      <p className="help order-services__intro">{t('orderServices.intro')}</p>

      {/* Same icon-button cards as the product picker (family-grid /
          family-card). Shipping, Fulfillment and Pickup are the three
          mutually-exclusive delivery modes — at most one lit, none until the
          customer picks — with Pickup LAST. Each card carries its own one-line
          description, so the details it reveals below open straight onto their
          questions, with no heading or gloss repeating what the card already
          said. The wrapper carries DELIVERY_MODE_FIELD_KEY as its id, so an
          unanswered mode's reveal/scroll lands on the cards. */}
      <div
        id={DELIVERY_MODE_FIELD_KEY}
        tabIndex={-1}
        className={modeError ? 'form-row form-row--error' : undefined}
        role="group"
        aria-label={t('orderServices.grid.ariaLabel')}
        aria-describedby={modeError ? `${DELIVERY_MODE_FIELD_KEY}-error` : undefined}
      >
        <div className="family-grid">
          {ORDER_SERVICES.map((svc) => {
            // Radio-like: clicking one selects it and drops the others; clicking
            // the active one is a no-op. Pickup is disabled while a physical
            // proof is on the quote (it needs a carrier).
            const sel = mode === svc;
            const pickupBlocked = svc === 'pickup' && physicalProofSelected;
            return (
              <button
                key={svc}
                type="button"
                aria-pressed={sel}
                aria-controls={`order-service-${svc}`}
                className={`family-card${sel ? ' family-card--selected' : ''}`}
                onClick={() => choose(svc)}
                disabled={disabled || pickupBlocked}
                title={pickupBlocked ? t('orderServices.pickup.disabledTitle') : undefined}
              >
                <span className="family-card__art" aria-hidden="true">
                  <OrderServiceGlyph service={svc} />
                </span>
                <span className="family-card__label">
                  {svc === 'pickup' ? t('orderServices.pickup.label') : ORDER_SERVICE_LABELS[svc]}
                </span>
                <span className="family-card__desc">{t(`orderServices.${svc}.description`)}</span>
              </button>
            );
          })}
        </div>
        {modeError && (
          <span className="field-error" id={`${DELIVERY_MODE_FIELD_KEY}-error`}>
            {modeError}
          </span>
        )}
      </div>
      {physicalProofSelected && (
        <p className="help help--indent">{t('orderServices.physicalProofNotice')}</p>
      )}

      {/* ── The printed sample: how many, and where they go (FB-0036) ──
          ABOVE the delivery subsections, and outside them: a sample is mailed
          whether the finished order ships, is fulfilled, or (impossible here —
          Pickup is disabled above) is collected, so it must not live inside the
          Shipping subsection that only one of those modes reveals. */}
      {physicalProofSelected && (
        <ProofDeliveryForm
          proofQuantity={proofQuantity ?? null}
          onProofQuantityChange={onProofQuantityChange}
          proofShipTos={proofShipTos}
          onProofShipTosChange={onProofShipTosChange}
          errors={errors}
          disabled={disabled}
        />
      )}

      {/* ── Shipping subsection (the addresses, via ShipToForm) ── */}
      {isShipping && (
        <OrderSubsection id="order-service-shipping">
          <EnumRadios
            fieldKey="order_services.shipping_service"
            legend={t('orderServices.shipping.serviceLegend')}
            options={SHIPPING_SERVICES}
            labels={shippingServiceLabels}
            value={value.shipping_service}
            onPick={pickShippingService}
            errors={errors}
            disabled={disabled}
          />
          {value.shipping_service === 'customer_labels' && (
            <ThirdPartyAccountField
              value={value.third_party_account ?? ''}
              onChange={(next) =>
                onChange({ ...value, third_party_account: next === '' ? undefined : next })
              }
              errors={errors}
              disabled={disabled}
            />
          )}
          {applyBarcode}
          <ShipToForm
            shipTos={shipTos}
            onChange={onShipTosChange}
            expectedTotal={orderQuantity ?? null}
            errors={errors}
            disabled={disabled}
          />
        </OrderSubsection>
      )}

      {/* ── Pickup subsection: a reassurance, plus the barcode add-on ── */}
      {isPickup && (
        <OrderSubsection id="order-service-pickup">
          <p className="help help--flush">{t('orderServices.pickup.body')}</p>
          {applyBarcode}
        </OrderSubsection>
      )}

      {/* ── Fulfillment subsection ── */}
      {isFulfillment && (
        <OrderSubsection id="order-service-fulfillment">
          <FulfillmentFields
            fulfillment={fulfillment}
            onPatchFulfillment={patchFulfillment}
            items={fulfillmentItems}
            {...(onPatchItemFulfillmentTarget
              ? { onPatchItemTarget: onPatchItemFulfillmentTarget }
              : {})}
            errors={errors}
            disabled={disabled}
          />
          <p className="help help--flush">{t('orderServices.fulfillment.footerHelp')}</p>
        </OrderSubsection>
      )}
    </div>
  );
}

// The customer's own carrier account (FB-0221), optional: a customer sending
// prepaid labels may have none. Its DOM id is its field key, like every other
// control in this section, so a refused save lands on it.
function ThirdPartyAccountField({
  value,
  onChange,
  errors,
  disabled,
}: {
  value: string;
  onChange: (next: string) => void;
  errors: Record<string, string>;
  disabled?: boolean;
}): JSX.Element {
  const { t } = useTranslation(['quote']);
  const domId = useFieldDomId(THIRD_PARTY_ACCOUNT_FIELD_KEY);
  const err = errors[THIRD_PARTY_ACCOUNT_FIELD_KEY];
  return (
    <div className={`form-row${err ? ' form-row--error' : ''}`}>
      <label htmlFor={domId}>
        {t('orderServices.shipping.accountLabel')}
        {!disabled && <span className="help"> {t('shipTo.fields.optionalSuffix')}</span>}
      </label>
      {disabled ? (
        <p className="value--locked" id={domId}>
          {readBackValue(value)}
        </p>
      ) : (
        <input
          id={domId}
          type="text"
          autoComplete="off"
          spellCheck={false}
          maxLength={THIRD_PARTY_ACCOUNT_MAX_LENGTH}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          aria-invalid={err ? true : undefined}
          aria-describedby={err ? `${domId}-error` : undefined}
        />
      )}
      {err && (
        <span className="field-error" id={`${domId}-error`}>
          {err}
        </span>
      )}
    </div>
  );
}
